"use client";
import {
  InfoCircleOutlined,
  UploadOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import CalendarIcon from "@untitled-ui/icons-react/build/esm/Calendar";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import {
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Select,
  Tooltip,
  Upload,
} from "antd";
import { useMemo, useState } from "react";

const collateralTypeOptions = [
  { value: "vehicle_logbook", label: "Vehicle logbook" },
  { value: "land_title", label: "Land title deed" },
  { value: "building", label: "Building / property" },
  { value: "fixed_deposit", label: "Fixed deposit lien" },
  { value: "equipment", label: "Equipment / machinery" },
];

const relationshipOptions = [
  { value: "spouse", label: "Spouse" },
  { value: "parent", label: "Parent" },
  { value: "sibling", label: "Sibling" },
  { value: "employer", label: "Employer" },
  { value: "business_partner", label: "Business partner" },
  { value: "other", label: "Other" },
];

const sectionHeaderStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#475467",
  letterSpacing: "0.05em",
  marginBottom: 16,
};

const sectionDividerStyle = {
  borderTop: "1px dashed #e5e7eb",
  paddingTop: 24,
  marginTop: 24,
};

const documentSlots = [
  {
    key: "validId",
    title: "Valid ID / Passport",
    subtitle: "Click to upload PDF or image",
  },
  {
    key: "proofOfIncome",
    title: "Proof of income (6 months)",
    subtitle: "Click to upload PDF or image",
  },
  {
    key: "bankStatement",
    title: "Bank statement (6 months)",
    subtitle: "Click to upload PDF or image",
  },
  {
    key: "collateralEvidence",
    title: "Collateral evidence",
    subtitle: "Vehicle logbook, C of O, etc.",
  },
  {
    key: "offerAcceptance",
    title: "Offer acceptance letter",
    subtitle: "Signed by applicant",
  },
  {
    key: "guarantorDocs",
    title: "Guarantor documents",
    subtitle: "ID + income evidence",
  },
];

const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/jpg",
];
const MAX_SIZE_MB = 10;

const CollateralDocs = (props) => {
  const { onFinish, form, initialValues } = props;

  const [uploadedFiles, setUploadedFiles] = useState({});

  const collateralValue = Form.useWatch("collateralValue", form);
  const loanAmount = Form.useWatch("loanAmount", form);

  const ltvRatio = useMemo(() => {
    const loan = parseFloat(loanAmount) || 0;
    const collateral = parseFloat(collateralValue) || 0;
    if (collateral <= 0) return 0;
    return (loan / collateral) * 100;
  }, [loanAmount, collateralValue]);

  const handleBeforeUpload = (file, slotKey) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error("Only PDF, JPG, and PNG files are allowed");
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
      message.error(`File must be smaller than ${MAX_SIZE_MB}MB`);
      return Upload.LIST_IGNORE;
    }

    setUploadedFiles((prev) => ({
      ...prev,
      [slotKey]: file,
    }));

    return false;
  };

  const renderDocumentCard = (slot) => {
    const uploaded = uploadedFiles[slot.key];

    return (
      <Col span={12} style={{ padding: "0px 8px", marginBottom: 16 }} key={slot.key}>
        <Upload
          showUploadList={false}
          beforeUpload={(file) => handleBeforeUpload(file, slot.key)}
          accept=".pdf,.jpg,.jpeg,.png"
        >
          <div
            style={{
              border: uploaded
                ? "1px dashed #86efac"
                : "1px dashed #d0d5dd",
              borderRadius: 12,
              padding: 16,
              minHeight: 80,
              cursor: "pointer",
              background: uploaded ? "#f0fdf4" : "#fff",
              display: "flex",
              alignItems: "center",
              gap: 12,
              transition: "all 0.2s",
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: uploaded ? "#dcfce7" : "#f2f4f7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {uploaded ? (
                <FileTextOutlined
                  style={{ fontSize: 18, color: "#16a34a" }}
                />
              ) : (
                <UploadOutlined
                  style={{ fontSize: 18, color: "#667085" }}
                />
              )}
            </div>
            <div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#344054",
                }}
              >
                {slot.title}
              </div>
              <div style={{ fontSize: 12, color: "#667085", marginTop: 2 }}>
                {uploaded ? uploaded.name : slot.subtitle}
              </div>
            </div>
          </div>
        </Upload>
      </Col>
    );
  };

  return (
    <div>
      <Form
        className="custom-form"
        layout="vertical"
        onFinish={(values) => {
          onFinish({ ...values, documents: uploadedFiles });
        }}
        form={form}
        initialValues={initialValues}
        style={{ width: "100%" }}
      >
        <div style={sectionHeaderStyle}>COLLATERAL DETAILS</div>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Collateral Type
                  <Tooltip title="Select the type of collateral">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="collateralType"
              rules={[
                {
                  required: true,
                  message: "Collateral type is required",
                },
              ]}
            >
              <Select
                placeholder="Select collateral type"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={collateralTypeOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Collateral Description
                  <Tooltip title="Describe the collateral asset">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="collateralDescription"
              rules={[
                {
                  required: true,
                  message: "Collateral description is required",
                },
              ]}
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="Describe the collateral asset"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Estimated Value
                  <Tooltip title="Enter the estimated value of the collateral">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="collateralValue"
              rules={[
                {
                  required: true,
                  message: "Estimated value is required",
                },
              ]}
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                addonBefore={
                  <span style={{ fontWeight: 500, color: "#344054" }}>
                    {"₦"}
                  </span>
                }
                placeholder="0.00"
                type="number"
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  LTV Ratio
                  <Tooltip title="Loan-to-value ratio">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="ltvRatio"
              help="within 75% limit"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="Computed automatically"
                readOnly
                value={ltvRatio > 0 ? ltvRatio.toFixed(1) + "%" : ""}
                suffix={
                  ltvRatio > 0 ? (
                    <span
                      style={{
                        color: ltvRatio <= 75 ? "#059669" : "#dc2626",
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      {ltvRatio.toFixed(1)}%
                    </span>
                  ) : null
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Valuation Date
                  <Tooltip title="Select the collateral valuation date">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="valuationDate"
              rules={[
                {
                  required: true,
                  message: "Valuation date is required",
                },
              ]}
            >
              <DatePicker
                className="bond-date-picker"
                style={{
                  width: "100%",
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 11px",
                  fontSize: 14,
                }}
                suffixIcon={<CalendarIcon color="#667085" width={20} />}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* DOCUMENT UPLOAD */}
        <div style={sectionDividerStyle}>
          <div style={sectionHeaderStyle}>DOCUMENT UPLOAD</div>
        </div>

        <Row>{documentSlots.map(renderDocumentCard)}</Row>

        {/* GUARANTOR (OPTIONAL) */}
        <div style={sectionDividerStyle}>
          <div style={sectionHeaderStyle}>GUARANTOR (OPTIONAL)</div>
        </div>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Guarantor Full Name
                  <Tooltip title="Enter the guarantor's full legal name">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="guarantorFullName"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="Full legal name"
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Relationship to Applicant
                  <Tooltip title="Select relationship to the loan applicant">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="guarantorRelationship"
            >
              <Select
                placeholder="Select relationship"
                style={{ height: 44 }}
                suffixIcon={<ChevronDown color="#667085" width={20} />}
                options={relationshipOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Guarantor Phone
                  <Tooltip title="Enter the guarantor's phone number">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="guarantorPhone"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="+234..."
              />
            </Form.Item>
          </Col>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Guarantor BVN
                  <Tooltip title="Enter the guarantor's 11-digit BVN">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="guarantorBvn"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                placeholder="11-digit BVN"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <span>
                  Monthly Income
                  <Tooltip title="Enter the guarantor's monthly income">
                    <InfoCircleOutlined
                      style={{ marginLeft: "4px", color: "#667085" }}
                    />
                  </Tooltip>
                </span>
              }
              name="guarantorMonthlyIncome"
            >
              <Input
                className="bond-input"
                style={{ height: 44 }}
                addonBefore={
                  <span style={{ fontWeight: 500, color: "#344054" }}>
                    {"₦"}
                  </span>
                }
                placeholder="0.00"
                type="number"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CollateralDocs;
